import React, { useEffect, useState } from 'react';

import { Skeleton, Typography } from '@mui/material';

import Button from '../../../../components/Button';
import ContentCard from '../../../../components/ContentCard';
import PermissionsList from '../../../../components/PermissionsList';
import { useGetPermissionsQuery, useUnsharePermissionMutation } from '../../../../services/permissions';
import { Permission, ShortPermission } from '../../../../services/permissions/types';
import { PermissionSource, PermissionType } from '../../../../types/common';
import {
  CLASS_PERMISSION_GROUP_PREFIX, PERMISSION_GROUP_PREFIX,
} from '../../../../utils/common/constants';
import useSuccess from '../../../../utils/react/hooks/useSuccess';
import { MasterAccessContainer } from '../../index.styles';

interface PermissionsCardProps {
  user?: {
    id: string,
    tutorialPermissions?: ShortPermission[],
    videoPermissions?: ShortPermission[],
    notePermissions?: ShortPermission[],
    examPermissions?: ShortPermission[],
    hasMasterAccess?: boolean,
  },
}

const PermissionsCard = ({ user } : PermissionsCardProps) => {
  const [targetPermissionId, setTargetPermissionId] = useState<string | undefined>(undefined);
  const [unsharePermission, {
    isLoading: isUnsharePermissionLoading,
    isSuccess: isUnsharePermissionSuccess,
  }] = useUnsharePermissionMutation();

  const { data: allPermissions } = useGetPermissionsQuery({});
  const [permissions, setPermissions] = useState<{
    id: string,
    label: string,
    value: string
  }[]>([]);

  useEffect(() => {
    if (allPermissions) {
      setPermissions([
        ...(user?.tutorialPermissions || []),
        ...(user?.videoPermissions || []),
        ...(user?.notePermissions || []),
        ...(user?.examPermissions || []),
      ].map((x) => ({
        ...x,
        ...allPermissions.data.find((p) => p.value === x.value),
      } as Permission)));
    }
  }, [allPermissions, user]);

  useSuccess(isUnsharePermissionSuccess, 'Permission removed');

  if (!user) {
    return (
      <Skeleton variant="rectangular" height={128} />
    );
  }

  return (
    <ContentCard variant="outlined" title="Permissions">
      {user.hasMasterAccess ? (
        <MasterAccessContainer>
          <Typography variant="overline">This user has master access</Typography>
          <Button
            size="medium"
            onClick={() => unsharePermission({
              tag: PermissionType.Base,
              userId: user.id,
            })}
            loading={isUnsharePermissionLoading}
            color="error"
          >Remove Master Access
          </Button>
        </MasterAccessContainer>
      ) : (
        <PermissionsList
          data={permissions}
          columns={[{
            field: 'label',
            headerName: 'Label',
            width: 450,
          }, {
            field: 'source',
            headerName: 'Source',
            width: 100,
            renderCell: ({ row }: any) => {
              if (row.sources.length > 1) return 'Various';
              if (row.sources[0] === PermissionSource.Direct) return 'Direct';
              if (row.sources[0].startsWith(PERMISSION_GROUP_PREFIX)) return 'Group';
              if (row.sources[0].startsWith(CLASS_PERMISSION_GROUP_PREFIX)) return 'Class';
              return 'Unknown';
            },
          }, {
            field: 'control',
            headerName: 'Control',
            width: 100,
            renderCell: ({ row }: any) => row.sources[0] === 'DIRECT' && (
              <Button
                size="small"
                color="error"
                variant="text"
                allCaps
                disabled={isUnsharePermissionLoading}
                loading={isUnsharePermissionLoading
                                    && targetPermissionId === row.id}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  setTargetPermissionId(row.id);
                  unsharePermission({
                    userId: user.id,
                    tag: row.id,
                  });
                }}
              >
                Unshare
              </Button>
            ),
          }]}
          pageSize={10}
          getRowHref={(row: Permission) => `/admin/permission/${row.id}`}
        />
      )}
    </ContentCard>
  );
};

export default PermissionsCard;
